import { useState } from 'react';

import { useQueryClient } from 'react-query';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import type { VerificationChannel } from '@identity/domain/types';
import { IdentityWizardLayout } from '@identity/presentation/components/base';
import type { PhoneVerificationHandoff } from '@identity/presentation/components/specific/ProfileContactCard';
import { PhoneAwaitingView, PhoneCaptureView } from '@identity/presentation/components/specific/steps';
import { accountStatusKey, PROFILE_ROUTE } from '@identity/presentation/constants';
import { useIdentityUserId } from '@identity/presentation/hooks';

import { PhoneVerifiedScreen } from './PhoneVerifiedScreen';
import { VerificationSupersededScreen } from './VerificationSupersededScreen';

/** The screen showing right now. */
type WizardScreen = 'capture' | 'awaiting' | 'verified' | 'superseded';

/** The link that is being waited for: where it went and which verification it opened. */
type SentLink = {
  channel: VerificationChannel;
  phone: string;
  verificationUuid?: string;
};

/**
 * Verifying a phone changed from Mi perfil: the number is confirmed here, the link is sent from
 * here, and the wait for it closes on one of two screens.
 *
 * The profile only hands over the number, through the router state and never through the URL: a
 * phone in the address bar stays in the history, in the referrers and in every access log, and the
 * observability rules of this domain forbid the whole number even in a log of our own.
 *
 * Losing that state — a reload, a direct visit — sends the user back to the profile, which is the
 * one place that knows the registered number.
 */
export const PhoneVerificationWizard = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const userId = useIdentityUserId();
  const { state } = useLocation<PhoneVerificationHandoff | undefined>();

  const [sent, setSent] = useState<SentLink | null>(null);
  const [screen, setScreen] = useState<WizardScreen>('capture');

  if (!state?.phone) return <Redirect to={PROFILE_ROUTE} />;

  // A send and a resend both open a verification, and with it a wait that starts over on each one.
  const startWait = (link: SentLink) => {
    setSent(link);
    setScreen('awaiting');
  };

  // refetchInactive because Mi perfil is unmounted while this wizard is on screen: without it the
  // entry is only marked stale and the profile paints the old number before its refetch answers.
  const onVerified = () => {
    queryClient.invalidateQueries(accountStatusKey(userId), { refetchInactive: true });
    setScreen('verified');
  };

  return (
    <IdentityWizardLayout>
      {screen === 'capture' && (
        <PhoneCaptureView
          defaultPhone={sent?.phone ?? state.phone}
          onCancel={() => history.replace(PROFILE_ROUTE)}
          onSent={startWait}
        />
      )}

      {screen === 'awaiting' && sent && (
        <PhoneAwaitingView
          channel={sent.channel}
          onResent={({ channel, verificationUuid }) => startWait({ ...sent, channel, verificationUuid })}
          onSuperseded={() => setScreen('superseded')}
          onUseAnotherNumber={() => setScreen('capture')}
          onVerified={onVerified}
          phone={sent.phone}
          verificationUuid={sent.verificationUuid}
        />
      )}

      {screen === 'verified' && <PhoneVerifiedScreen />}

      {screen === 'superseded' && <VerificationSupersededScreen />}
    </IdentityWizardLayout>
  );
};
